import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Loader2, MapPin, User, Users, Star } from 'lucide-react';
import { Layout } from './Home';
import PageHero from '../components/PageHero';
import { listPeople, resolveFileUrl } from '../services/api';

const CATS = [
  { key: 'board', label: 'Board of Directors', icon: Star, eyebrow: 'Governance' },
  { key: 'management', label: 'Management', icon: Users, eyebrow: 'Executive Team' },
  { key: 'key_personnel', label: 'Key Personnel', icon: User, eyebrow: 'Specialists & Leads' },
];

const LeadershipPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listPeople()
      .then((r) => setItems(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const g = { board: [], management: [], key_personnel: [] };
    items.forEach((p) => {
      if (g[p.category]) g[p.category].push(p);
    });
    return g;
  }, [items]);

  return (
    <Layout>
      <PageHero
        eyebrow="Leadership"
        title="The people building Emerald Corporates."
        description="Four decades of experience across finance, technology, energy and resources — led by senior specialists on four continents."
        image="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=1600&q=80"
      />
      <section className="bg-[#e2ede6] py-16 lg:py-24" data-testid="leadership-page">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 space-y-20">
          {loading ? (
            <div className="text-center text-[#0b1a2e]/60">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="bg-white rounded-2xl border border-black/5 p-12 text-center">
              <User className="w-10 h-10 mx-auto text-[#0b1a2e]/30 mb-3" />
              <p className="text-[#0b1a2e]/60">Leadership profiles will be published here shortly.</p>
            </div>
          ) : (
            CATS.map((c) => (
              <div key={c.key} data-testid={`leadership-group-${c.key}`}>
                <div className="flex items-baseline justify-between mb-8">
                  <div>
                    <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">
                      {c.eyebrow}
                    </span>
                    <h2
                      className="mt-2 text-3xl lg:text-4xl text-[#0b1a2e]"
                      style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}
                    >
                      {c.label}
                    </h2>
                  </div>
                </div>
                {grouped[c.key].length === 0 ? (
                  <div className="bg-white/60 rounded-xl p-6 text-center text-sm text-[#0b1a2e]/50">
                    No {c.label.toLowerCase()} listed yet.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {grouped[c.key].map((p) => (
                      <Link
                        key={p.id}
                        to={`/people/${p.slug}`}
                        data-testid="person-card"
                        className="group bg-white rounded-2xl overflow-hidden border border-black/5 hover:shadow-xl transition-all"
                      >
                        <div className="aspect-square overflow-hidden bg-[#e2ede6]">
                          {p.photo_url ? (
                            <img
                              src={resolveFileUrl(p.photo_url)}
                              alt={p.name}
                              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-[#0b1a2e]/25">
                              <User className="w-14 h-14" />
                            </div>
                          )}
                        </div>
                        <div className="p-5">
                          <div className="text-[10px] uppercase tracking-widest text-[#0f5c3d] font-semibold">
                            {p.designation || '—'}
                          </div>
                          <h3 className="mt-1 font-semibold text-[#0b1a2e] group-hover:text-[#0f5c3d] transition-colors">
                            {p.name}
                          </h3>
                          {p.location && (
                            <div className="mt-1 inline-flex items-center gap-1 text-xs text-[#0b1a2e]/60">
                              <MapPin className="w-3 h-3" /> {p.location}
                            </div>
                          )}
                          <p className="mt-2 text-sm text-[#0b1a2e]/70 line-clamp-3">{p.short_desc}</p>
                          <div className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-[#0f5c3d]">
                            View profile <ArrowRight className="w-3.5 h-3.5" />
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </section>
    </Layout>
  );
};

export default LeadershipPage;
